import { unsafeCSS, html } from "lit";
import { property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { formatNumber } from "../../utilities/format-number.js";
import { LeafElement } from "../../components/leaf-element.js";
import { LeafButton } from "../../components/button/button.component.js";
import { watch } from "../../utilities/watch.js";
import { dynamicPhoneNumberContext, analyticsReadyContext } from "./phone-number-context.js";
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let s$1 = class s extends Event {
  constructor(s3, t, e2) {
    super("context-request", { bubbles: true, composed: true }), this.context = s3, this.callback = t, this.subscribe = e2 ?? false;
  }
};
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class s2 {
  constructor(t, s3, i, h) {
    if (this.subscribe = false, this.provided = false, this.value = void 0, this.t = (t2, s4) => {
      this.unsubscribe && (this.unsubscribe !== s4 && (this.provided = false, this.unsubscribe()), this.subscribe || this.unsubscribe()), this.value = t2, this.host.requestUpdate(), this.provided && !this.subscribe || (this.provided = true, this.callback && this.callback(t2, s4)), this.unsubscribe = s4;
    }, this.host = t, void 0 !== s3.context) {
      const t2 = s3;
      this.context = t2.context, this.callback = t2.callback, this.subscribe = t2.subscribe ?? false;
    } else
      this.context = s3, this.callback = i, this.subscribe = h ?? false;
    this.host.addController(this);
  }
  hostConnected() {
    this.dispatchRequest();
  }
  hostDisconnected() {
    this.unsubscribe && (this.unsubscribe(), this.unsubscribe = void 0);
  }
  dispatchRequest() {
    this.host.dispatchEvent(new s$1(this.context, this.t, this.subscribe));
  }
}
/**
 * @license
 * Copyright 2022 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function c({ context: c2, subscribe: e2 }) {
  return (o, n) => {
    "object" == typeof n ? n.addInitializer(function() {
      new s2(this, { context: c2, callback: (t) => {
        this[n.name] = t;
      }, subscribe: e2 });
    }) : o.constructor.addInitializer((o2) => {
      new s2(o2, { context: c2, callback: (t) => {
        o2[n] = t;
      }, subscribe: e2 });
    });
  };
}
const styles = `/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

*,
::before,
::after {
  box-sizing: border-box;
}

:where(html) {
  block-size: 100%;
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  color: var(--leaf-color-content-default);
  background-color: var(--background-default);
  scrollbar-gutter: stable;
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(:not(:active):focus-visible) {
  outline-offset: 1px;
}

:where(body) {
  /* Improve text rendering */
  -webkit-font-smoothing: antialiased;
  /* https://marco.org/2012/11/15/text-rendering-optimize-legibility */
  text-rendering: optimizeSpeed;
  margin: 0;
  /* Allow percentage-based heights in the application */
  min-block-size: 100%;
  overflow-x: hidden;
}
:where(body).backdrop-active {
  overflow-y: hidden;
}

:where(p, h1, h2, h3, h4, h5, h6) {
  overflow-wrap: break-word;
  margin-block: var(--leaf-spacing-16) 0;
  margin-inline: 0;
  word-break: break-word;
  scroll-margin-top: var(--leaf-spacing-32);
  /* Target WebKit (Safari) browsers */
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):first-child {
  margin-block-start: 0;
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):last-child {
  margin-block-end: 0;
}
@supports (-webkit-touch-callout: none) {
  @media (max-width: 47.999rem) {
    :where(p, h1, h2, h3, h4, h5, h6) {
      scroll-margin-top: calc(var(--leaf-spacing-32) * 2);
    }
  }
}

:where(h1, h2, h3, h4, h5, h6):not([slot]) {
  color: var(--leaf-color-content-brand-strong);
}

:where(h1) {
  font-family: var(--leaf-typography-display-default-mobile-font-family);
  font-size: var(--leaf-typography-display-default-mobile-font-size);
  font-weight: var(--leaf-typography-display-default-mobile-font-weight);
  line-height: var(--leaf-typography-display-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-display-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h1) {
    font-family: var(--leaf-typography-display-default-font-family);
    font-size: var(--leaf-typography-display-default-font-size);
    font-weight: var(--leaf-typography-display-default-font-weight);
    line-height: var(--leaf-typography-display-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-display-default-text-decoration);
            text-decoration: var(--leaf-typography-display-default-text-decoration);
    text-transform: var(--leaf-typography-display-default-text-transform);
  }
}

:where(h2) {
  font-family: var(--leaf-typography-headline-large-mobile-font-family);
  font-size: var(--leaf-typography-headline-large-mobile-font-size);
  font-weight: var(--leaf-typography-headline-large-mobile-font-weight);
  line-height: var(--leaf-typography-headline-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h2) {
    font-family: var(--leaf-typography-headline-large-font-family);
    font-size: var(--leaf-typography-headline-large-font-size);
    font-weight: var(--leaf-typography-headline-large-font-weight);
    line-height: var(--leaf-typography-headline-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-large-text-decoration);
            text-decoration: var(--leaf-typography-headline-large-text-decoration);
    text-transform: var(--leaf-typography-headline-large-text-transform);
  }
}

:where(h3) {
  font-family: var(--leaf-typography-headline-default-mobile-font-family);
  font-size: var(--leaf-typography-headline-default-mobile-font-size);
  font-weight: var(--leaf-typography-headline-default-mobile-font-weight);
  line-height: var(--leaf-typography-headline-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h3) {
    font-family: var(--leaf-typography-headline-default-font-family);
    font-size: var(--leaf-typography-headline-default-font-size);
    font-weight: var(--leaf-typography-headline-default-font-weight);
    line-height: var(--leaf-typography-headline-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-default-text-decoration);
            text-decoration: var(--leaf-typography-headline-default-text-decoration);
    text-transform: var(--leaf-typography-headline-default-text-transform);
  }
}

:where(h4) {
  font-family: var(--leaf-typography-headline-small-mobile-font-family);
  font-size: var(--leaf-typography-headline-small-mobile-font-size);
  font-weight: var(--leaf-typography-headline-small-mobile-font-weight);
  line-height: var(--leaf-typography-headline-small-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-small-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h4) {
    font-family: var(--leaf-typography-headline-small-font-family);
    font-size: var(--leaf-typography-headline-small-font-size);
    font-weight: var(--leaf-typography-headline-small-font-weight);
    line-height: var(--leaf-typography-headline-small-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-small-text-decoration);
            text-decoration: var(--leaf-typography-headline-small-text-decoration);
    text-transform: var(--leaf-typography-headline-small-text-transform);
  }
}

:where(h5) {
  font-family: var(--leaf-typography-title-large-mobile-font-family);
  font-size: var(--leaf-typography-title-large-mobile-font-size);
  font-weight: var(--leaf-typography-title-large-mobile-font-weight);
  line-height: var(--leaf-typography-title-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-title-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h5) {
    font-family: var(--leaf-typography-title-large-font-family);
    font-size: var(--leaf-typography-title-large-font-size);
    font-weight: var(--leaf-typography-title-large-font-weight);
    line-height: var(--leaf-typography-title-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-title-large-text-decoration);
            text-decoration: var(--leaf-typography-title-large-text-decoration);
    text-transform: var(--leaf-typography-title-large-text-transform);
  }
}

:where(h6) {
  font-family: var(--leaf-typography-title-default-font-family);
  font-size: var(--leaf-typography-title-default-font-size);
  font-weight: var(--leaf-typography-title-default-font-weight);
  line-height: var(--leaf-typography-title-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-default-text-decoration);
          text-decoration: var(--leaf-typography-title-default-text-decoration);
  text-transform: var(--leaf-typography-title-default-text-transform);
}

:where(small) {
  font-family: var(--leaf-typography-body-small-font-family);
  font-size: var(--leaf-typography-body-small-font-size);
  font-weight: var(--leaf-typography-body-small-font-weight);
  line-height: var(--leaf-typography-body-small-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-small-text-decoration);
          text-decoration: var(--leaf-typography-body-small-text-decoration);
  text-transform: var(--leaf-typography-body-small-text-transform);
}

:where(u, ins, abbr) {
  text-underline-offset: 3px;
  text-decoration-thickness: var(--leaf-border-width-default);
}
@supports (-moz-appearance: none) {
  :where(u, ins, abbr) {
    text-underline-offset: 3px;
  }
}

:where(a[href], area, button, input, label[for], select, summary, textarea, [tabindex]:not([tabindex*="-"])) {
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

:where(form) leaf-input:not([type=hidden]),
:where(form) leaf-select:not([type=hidden]),
:where(form) leaf-textarea:not([type=hidden]),
:where(form) leaf-radiogroup:not([type=hidden]),
:where(form) leaf-checkgroup:not([type=hidden]),
:where(form) leaf-inputgroup:not([type=hidden]),
:where(form) leaf-button:not([type=hidden]),
:where(form) > leaf-label:not([type=hidden]) {
  margin-top: var(--leaf-spacing-24);
  max-width: 100%;
}
:where(form) leaf-button {
  margin-top: var(--leaf-spacing-16);
}
:where(form) leaf-label + leaf-input:not([type=hidden]),
:where(form) leaf-label + leaf-select:not([type=hidden]),
:where(form) leaf-label + leaf-textarea:not([type=hidden]) {
  margin-top: var(--leaf-spacing-4);
}
:where(form) leaf-label + leaf-radiogroup:not([type=hidden]),
:where(form) leaf-label + leaf-checkgroup:not([type=hidden]),
:where(form) leaf-label + leaf-inputgroup:not([type=hidden]) {
  margin-top: var(--leaf-spacing-8);
}

:where([href^=tel]) {
  white-space: nowrap;
}
@media (min-width: 48.001rem) {
  :where([href^=tel]) {
    border-bottom: 0 !important;
    color: inherit !important;
    cursor: default !important;
    font-weight: inherit !important;
    padding-bottom: 0 !important;
    pointer-events: none;
  }
}

:where(a) {
  font-family: var(--leaf-typography-body-default-link-font-family);
  font-size: inherit;
  font-weight: var(--leaf-typography-body-default-link-font-weight);
  line-height: inherit;
  -webkit-text-decoration: var(--leaf-text-decoration-none);
          text-decoration: var(--leaf-text-decoration-none);
  text-transform: var(--leaf-typography-body-default-link-text-transform);
  border-bottom: var(--leaf-border-width-default) solid;
  color: var(--leaf-color-content-link);
  word-break: break-word;
}
:where(a):hover {
  color: var(--leaf-color-content-link-hover);
}
:where(a):focus {
  background-color: var(--leaf-color-bg-active);
  border-color: var(--leaf-color-border-transparent);
  color: var(--leaf-color-content-knockout);
  outline: none;
}
:where(a):where(.leaf-u-link--external)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-4);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
}
:where(a):where(.leaf-u-link--lock)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-8);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: var(--leaf-spacing-40);
          mask-size: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--inverted) {
  color: var(--leaf-color-content-knockout);
}
:where(a):where(.leaf-u-link--inverted):hover {
  color: var(--leaf-color-content-brand-knockout);
}
:where(a):where(.leaf-u-link--inverted):focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-active);
}
:where(a):where(.leaf-u-link--action) {
  border-bottom: none;
  display: inline-block;
  line-height: var(--leaf-spacing-32);
  margin: var(--leaf-spacing-8) calc(var(--leaf-spacing-48) - var(--leaf-spacing-4));
  margin-left: 0;
  position: relative;
}
:where(a):where(.leaf-u-link--action)::before {
  content: "";
  display: inline-block;
  height: 100%;
  min-height: var(--leaf-spacing-40);
  vertical-align: middle;
}
:where(a):where(.leaf-u-link--action)::after {
  background-color: currentColor;
  bottom: 0;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3C/svg%3E");
  height: var(--leaf-spacing-40);
  margin-left: var(--leaf-spacing-4);
  position: absolute;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  width: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--action):focus:after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20.2803 12L18.8703 13.41L24.4503 19H12.2803V21H24.4503L18.8703 26.59L20.2803 28L28.2803 20L20.2803 12Z' fill='white'/%3E%3Crect x='39.2803' y='39' width='38' height='38' rx='19' transform='rotate(180 39.2803 39)' stroke='black' stroke-width='2'/%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted)::after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3C/svg%3E");
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):hover::after {
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):focus:after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='white' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}

:where(span) {
  /* is this needed ? */
  /* https://stackoverflow.com/questions/1556363/weird-span-element-rendering-in-safari-on-iphone */
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(img, svg, video, canvas, audio, iframe, embed, object) {
  display: block;
}

:where(img, svg, video) {
  max-inline-size: 100%;
  block-size: auto;
}

:where(strong, b, dt, summary) {
  font-weight: var(--leafdcws-font-weight-bold);
}

:where(p, address, ol, ul, dl, dt:not(:first-of-type)) {
  margin-block-start: var(--leaf-spacing-16);
  margin-block-end: 0;
}

:where(address) {
  font-style: normal;
}

:where(ol, ul) {
  padding-inline-start: var(--leaf-spacing-16);
}

:where(ol ol, ul ul, ol ul, ul ol) {
  margin-block: 0;
}

:where(ul > li > ul > li) {
  list-style-type: circle;
}
:where(ul > li > ul > li) ul > li {
  list-style-type: square;
}
:where(ul > li > ul > li) ul > li ul > li {
  list-style-type: disc;
}

:where(ul) {
  list-style-type: disc;
}

:where(ol) {
  list-style-type: decimal;
}

:where(ol > li > ol) {
  list-style-type: lower-alpha;
}

:where(ol > li > ol > li > ol) {
  list-style-type: lower-roman;
}

:where(li) {
  padding-inline-start: 0;
}

.leaf-c-content-block ul,
.leaf-c-content-block ol {
  display: flex;
  flex-direction: column;
  gap: var(--leaf-spacing-16);
  padding-inline-start: var(--leaf-spacing-32);
}

.visually-hidden {
  clip: rect(0 0 0 0);
  -webkit-clip-path: inset(50%);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.no-focus-visible-outline:focus-visible {
  outline: none;
}

:host {
  white-space: nowrap;
}

@media (min-width: 48rem) {
  .chc-c-phone-number--mobileonly {
    display: none;
  }
}

.chc-c-phone-number__link {
  white-space: normal;
}
@media (min-width: 48rem) {
  .chc-c-phone-number__link {
    pointer-events: none;
    color: inherit;
    cursor: default;
    font-weight: inherit;
    text-decoration: none;
  }
  .chc-c-phone-number__link:focus {
    color: var(--leaf-color-content-knockout) !important;
  }
}

.chc-c-phone-number--inverted {
  color: var(--leaf-color-content-knockout);
}`;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _CHCPhoneNumber = class _CHCPhoneNumber extends e(LeafElement) {
  constructor() {
    super(...arguments);
    this.separator = "-";
    this.inverted = false;
    this.parentheses = false;
    this.isButton = false;
    this.mobileOnly = false;
    this.campaignOptOut = false;
    this.analyticsReady = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this.classList.add("leaf-u-visually-hidden");
  }
  async updateDynamicPhoneNumber() {
    if (this.dynamicPhoneNumber && !this.campaignOptOut) {
      this.number = this.dynamicPhoneNumber;
      await this.updateComplete;
      this.sendEvent();
    }
  }
  analyticsLoaded() {
    this.sendEvent();
  }
  sendEvent() {
    if (this.dynamicPhoneNumber && this.analyticsReady) {
      this.emit("cipublic-phone-loaded", { detail: { phone: this } }, window);
    }
  }
  async updated() {
    await this.updateComplete;
    this.classList.remove("leaf-u-visually-hidden");
    this.setAttribute("data-element-text", formatNumber(this.number, this.separator, this.parentheses));
  }
  render() {
    const componentClassName = {
      "chc-c-phone-number": true,
      "chc-c-phone-number__link": !this.isButton,
      "chc-c-phone-number--mobileonly": this.mobileOnly,
      "chc-c-phone-number--inverted": this.inverted
    };
    let text;
    this.initialChildren = this.initialChildren || Array.from(this.childNodes);
    if (this.initialChildren.length) {
      text = this.initialChildren;
    } else {
      text = formatNumber(this.number, this.separator, this.parentheses);
    }
    if (this.isButton) {
      return html`<leaf-button href="tel:+${formatNumber(this.number, "")}" class=${classMap(componentClassName)}
        >${text}</leaf-button
      >`;
    }
    return html`<a href="tel:+${formatNumber(this.number, "")}" class=${classMap(componentClassName)}>${text}</a>`;
  }
};
_CHCPhoneNumber.styles = unsafeCSS(styles);
_CHCPhoneNumber.elementDefinitions = {
  "leaf-button": LeafButton
};
let CHCPhoneNumber = _CHCPhoneNumber;
__decorateClass([
  property({ reflect: true })
], CHCPhoneNumber.prototype, "number", 2);
__decorateClass([
  property({ reflect: true })
], CHCPhoneNumber.prototype, "separator", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], CHCPhoneNumber.prototype, "inverted", 2);
__decorateClass([
  property({ reflect: true, type: Boolean })
], CHCPhoneNumber.prototype, "parentheses", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "is-button" })
], CHCPhoneNumber.prototype, "isButton", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "is-mobile-only" })
], CHCPhoneNumber.prototype, "mobileOnly", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "campaign-opt-out" })
], CHCPhoneNumber.prototype, "campaignOptOut", 2);
__decorateClass([
  c({ context: dynamicPhoneNumberContext, subscribe: true }),
  state()
], CHCPhoneNumber.prototype, "dynamicPhoneNumber", 2);
__decorateClass([
  c({ context: analyticsReadyContext, subscribe: true }),
  state()
], CHCPhoneNumber.prototype, "analyticsReady", 2);
__decorateClass([
  watch("dynamicPhoneNumber", { lifecycleMethod: "willUpdate" })
], CHCPhoneNumber.prototype, "updateDynamicPhoneNumber", 1);
__decorateClass([
  watch("analyticsReady", { lifecycleMethod: "willUpdate" })
], CHCPhoneNumber.prototype, "analyticsLoaded", 1);
export {
  CHCPhoneNumber
};
//# sourceMappingURL=phone-number.component.js.map
